import { useRef, useCallback, useState } from 'react';
import { usePlaceContext } from '../../components/itinerary/PlaceContext';
import useApi from '../../Api';
import debounce from 'lodash/debounce';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import useCreateItinerary from '../../components/itinerary/useCreateItinerary';

const useItinerary = placeDetails => {
  const { user, token } = useAuth();
  const {
    state,
    setSelectedItinerary,
    setItineraries,
    renameItinerary,
    deleteItinerary,
    updateItineraryPrivacy
  } = usePlaceContext();
  const { itineraries, selectedItinerary } = state;
  const apiClient = useApi();
  const navigate = useNavigate();

  const {
    createItinerary,
    isCreating: isCreatingItinerary,
    modals: createModals
  } = useCreateItinerary();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [primaryActionLabel, setPrimaryActionLabel] = useState('');
  const [isRenamingItinerary, setIsRenamingItinerary] = useState(false);
  const [isDeletingItinerary, setIsDeletingItinerary] = useState(false);
  const [isUpdatingPrivacy, setIsUpdatingPrivacy] = useState(false);

  const renameTimeoutRef = useRef(null);
  const privacyDebounceRef = useRef(null);

  const handleCreateNewItinerary = () =>
    createItinerary({
      originId: placeDetails.origin_id,
      originName: placeDetails.name,
      editable: true
    });

  const handleRenameItinerary = (id, newName) => {
    if (renameTimeoutRef.current) clearTimeout(renameTimeoutRef.current);
    renameTimeoutRef.current = setTimeout(async () => {
      setIsRenamingItinerary(true);
      try {
        await apiClient.patch(
          `/itineraries/${id}/rename`,
          { name: newName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        renameItinerary(id, newName);
      } catch {
        setErrorMessage('Failed to rename itinerary. Please try again later.');
        setPrimaryActionLabel('Close');
        setShowErrorModal(true);
      } finally {
        setIsRenamingItinerary(false);
      }
    }, 300);
  };

  const handleDeleteItinerary = useCallback(
    debounce(async id => {
      setIsDeletingItinerary(true);
      try {
        await apiClient.delete(`/itineraries/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleteItinerary(id);
        if (selectedItinerary && selectedItinerary.id === id)
          setSelectedItinerary(null);
      } catch {
        setErrorMessage('Failed to delete itinerary. Please try again later.');
        setPrimaryActionLabel('Close');
        setShowErrorModal(true);
      } finally {
        setIsDeletingItinerary(false);
      }
    }, 200),
    [selectedItinerary, deleteItinerary, setSelectedItinerary, apiClient, token]
  );

  const handleSelectItinerary = itinerary => setSelectedItinerary(itinerary);

  const handleUpdatePrivacy = useCallback(
    (id, isPublic) =>
      new Promise((resolve, reject) => {
        if (privacyDebounceRef.current)
          clearTimeout(privacyDebounceRef.current);
        privacyDebounceRef.current = setTimeout(async () => {
          setIsUpdatingPrivacy(true);
          try {
            const res = await apiClient.patch(
              `/itineraries/${id}/privacy`,
              { is_public: isPublic },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            updateItineraryPrivacy(id, isPublic);
            resolve(res);
          } catch (error) {
            updateItineraryPrivacy(id, !isPublic);
            setErrorMessage('Failed to update privacy.');
            setPrimaryActionLabel('Close');
            setShowErrorModal(true);
            reject(error);
          } finally {
            setIsUpdatingPrivacy(false);
          }
        }, 500);
      }),
    [apiClient, token, updateItineraryPrivacy]
  );

  return {
    itineraries,
    selectedItinerary,
    handleCreateNewItinerary,
    handleSelectItinerary,
    handleRenameItinerary,
    handleDeleteItinerary,
    setSelectedItinerary,
    handleUpdatePrivacy,
    isCreatingItinerary,
    isRenamingItinerary,
    isDeletingItinerary,
    isUpdatingPrivacy,
    showErrorModal,
    errorMessage,
    primaryActionLabel,
    setShowErrorModal,
    setErrorMessage,
    setPrimaryActionLabel,
    createModals
  };
};

export default useItinerary;
